import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ButtonCustom from './ButtonCustom';
import ButtonIcon from './ButtonIcon';

interface GenericDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  cancelButtonText?: string;
  children: any;
  icon?: React.ReactNode;
  confirmButtonText?: any;
  onConfirm?: any;
  isLoading?: boolean;
  confirmDisabled?: boolean;
  // Optional sizing controls
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  fullScreen?: boolean;
  paperSx?: any;
  /** Merges into DialogContent (e.g. override global theme fixed width) */
  contentSx?: any;
  titleAlign?: 'center' | 'left' | 'right' | 'flex-start' | 'flex-end';
  contentAlign?: 'center' | 'left' | 'right' | 'start' | 'end' | 'flex-start' | 'flex-end';
  actionsAlign?: 'center' | 'space-between' | 'space-around' | 'flex-start' | 'flex-end';
  /** When set, replaces the default Cancel/Confirm footer buttons */
  actions?: React.ReactNode;
}

const GenericDialog: React.FC<GenericDialogProps> = ({ open, onClose, title, subtitle, children, icon, onConfirm, cancelButtonText, confirmButtonText, isLoading = false, confirmDisabled = false, maxWidth = 'md', fullWidth = true, fullScreen = false, paperSx, contentSx, titleAlign = 'center', contentAlign = 'center', actionsAlign = 'space-between', actions }) => {
  const { t } = useTranslation();
  const resolveTextAlign = (align: typeof titleAlign | typeof contentAlign) =>
    align === 'flex-start' ? 'start' : align === 'flex-end' ? 'end' : align;
  const resolvedTitleAlign = resolveTextAlign(titleAlign);
  const resolvedContentAlign = resolveTextAlign(contentAlign);
  const isContentStartAligned = contentAlign === 'flex-start' || contentAlign === 'start' || contentAlign === 'left';
  const iconJustify =
    resolvedTitleAlign === 'center' ? 'center'
      : resolvedTitleAlign === 'end' ? 'flex-end'
        : 'flex-start';
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" maxWidth={maxWidth} fullWidth={fullWidth} fullScreen={fullScreen} sx={{ marginBottom: "50px" }} PaperProps={{ sx: paperSx }}>
      <DialogTitle sx={{ textAlign: resolvedTitleAlign, pb: icon ? 0.5 : undefined }}>
        {icon && (
          <Box sx={{ display: 'flex', justifyContent: iconJustify, mb: 1 }}>
            {icon}
          </Box>
        )}
        <ButtonIcon
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </ButtonIcon>
      </DialogTitle>
      <DialogTitle id="alert-dialog-title" sx={{ pt: icon ? 0 : undefined }}>
        <Typography variant='h2' sx={{ textAlign: resolvedTitleAlign }}>
        {title}
        </Typography>
        {subtitle && (
          <Typography variant='body1' sx={{ mt: 1, color: 'text.secondary', textAlign: resolvedTitleAlign }}>
            {subtitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent
        className="dialog-content"
        sx={{
          width: '100%',
          ...(isContentStartAligned && {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }),
          ...contentSx,
        }}
      >
        <DialogContentText id="alert-dialog-description" sx={{ width: '100%' }}>
          <Typography textAlign={resolvedContentAlign} color='#2F3B49' fontSize={"18px"}>{children}</Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions dir={actions ? undefined : 'ltr'}  sx={{ 
        display: 'flex', 
        gap: 2, 
        justifyContent: actions || confirmButtonText ? actionsAlign : 'flex-end', 
        paddingBottom: "20px", 
        paddingX:"24px",
        '& > :not(style) ~ :not(style)': {
          marginLeft: '0 !important'
        },
        '& > *': {
          marginLeft: '0 !important'
        }
      }}>
        {actions ? (
          actions
        ) : (
          <>
            <ButtonCustom onClick={onClose} color='secondary' className='secondary-btn'
              variant="outlined"
              disabled={isLoading}
            >
              {cancelButtonText}
            </ButtonCustom>
            {confirmButtonText ? (
              <ButtonCustom onClick={onConfirm} color="primary" variant="contained"
                disabled={isLoading || confirmDisabled}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {confirmButtonText}
              </ButtonCustom>
            ) : null}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenericDialog;